<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import PerfilOficialSwiperHeader from '@/components/perfil-oficial/SwiperHeader.vue';
import { useFaq } from '~/composables/apostatudo/use-faq';

defineProps({
  headerTitle: {
    type: String,
    default: 'Dúvidas Frequentes',
  },
  headerIconPath: {
    type: String,
    default: 'home/duvidas',
  },
});

const { fetchFaq, categories, loading, hasCategories } = useFaq();

const activeTab = ref(0);
const dragged = ref(false);
const openQuestionId = ref<string | null>(null);

// Search
const searchOpen = ref(false);
const searchQuery = ref('');
const searchInputRef = ref<HTMLInputElement | null>(null);

const searchTabRef = ref<HTMLElement | null>(null);

function toggleSearch() {
  const isOpening = !searchOpen.value;

  searchOpen.value = isOpening;

  if (isOpening) {
    nextTick(() => {
      searchInputRef.value?.focus();
      if (searchTabRef.value) {
        searchTabRef.value.animate([{ width: '32px' }, { width: '200px' }], {
          duration: 350,
          easing: 'ease',
          fill: 'forwards',
        });
      }
    });
  } else if (searchTabRef.value) {
    searchTabRef.value.animate([{ width: '200px' }, { width: '32px' }], {
      duration: 300,
      easing: 'ease',
      fill: 'forwards',
    }).onfinish = () => {
      searchQuery.value = '';
      openQuestionId.value = null;
    };
  } else {
    searchQuery.value = '';
    openQuestionId.value = null;
  }
}

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const searchResults = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();

  if (!q) return [];

  const results: Array<{
    categoryName: string;
    id: string;
    question: string;
    answer: string;
  }> = [];

  for (const category of categories.value) {
    for (const item of category.items) {
      const inQuestion = item.question.toLowerCase().includes(q);
      const inAnswer = stripHtml(item.answer).toLowerCase().includes(q);

      if (inQuestion || inAnswer) {
        results.push({ categoryName: category.name, ...item });
      }
    }
  }

  return results;
});

// Refs para drag-to-scroll nas tabs
const tabsNavigation = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);

// Tab selecionada
const currentCategory = computed(() => {
  return categories.value[activeTab.value] || null;
});

function toggleQuestion(questionId: string) {
  const isOpening = openQuestionId.value !== questionId;
  const prevId = openQuestionId.value;

  // Fecha o anterior com animação
  if (prevId !== null && prevId !== questionId) {
    const prevEl = document.getElementById(`faq-body-${prevId}`);

    if (prevEl) {
      const prevHeight = prevEl.scrollHeight;

      prevEl.style.overflow = 'hidden';
      prevEl.animate(
        [
          { maxHeight: prevHeight + 'px', opacity: 1 },
          { maxHeight: '0px', opacity: 0 },
        ],
        {
          duration: 300,
          easing: 'ease',
          fill: 'forwards',
        },
      );
    }
  }

  openQuestionId.value = isOpening ? questionId : null;

  const el = document.getElementById(`faq-body-${questionId}`);

  if (!el) return;

  if (isOpening) {
    const height = el.scrollHeight;

    el.style.overflow = 'hidden';
    el.style.animation = 'none';

    // eslint-disable-next-line no-void
    void el.offsetHeight;

    el.animate(
      [
        { maxHeight: '0px', opacity: 0 },
        { maxHeight: height + 'px', opacity: 1 },
      ],
      {
        duration: 500,
        easing: 'ease',
        fill: 'forwards',
      },
    );
  } else {
    const height = el.scrollHeight;

    el.style.overflow = 'hidden';
    el.animate(
      [
        { maxHeight: height + 'px', opacity: 1 },
        { maxHeight: '0px', opacity: 0 },
      ],
      {
        duration: 400,
        easing: 'ease',
        fill: 'forwards',
      },
    );
  }
}

function selectTab(index: number) {
  if (dragged.value) {
    dragged.value = false;

    return;
  }

  // Se a busca está aberta, fecha e reseta sem animar troca de tab
  if (searchOpen.value) {
    searchOpen.value = false;
    searchQuery.value = '';
    openQuestionId.value = null;
    activeTab.value = index;
    // Anima fechamento do botão de busca
    if (searchTabRef.value) {
      searchTabRef.value.getAnimations().forEach((a) => a.cancel());
      searchTabRef.value.animate([{ width: '200px' }, { width: '32px' }], {
        duration: 300,
        easing: 'ease',
        fill: 'forwards',
      });
    }

    return;
  }

  if (activeTab.value === index) {
    return;
  }
  openQuestionId.value = null;

  const content = document.querySelector('.tabsFAQ__content') as HTMLElement;

  if (content) {
    content.animate(
      [
        { opacity: 1, transform: 'translateY(0)' },
        { opacity: 0, transform: 'translateY(-6px)' },
      ],
      { duration: 200, easing: 'ease-in', fill: 'forwards' },
    ).onfinish = () => {
      activeTab.value = index;

      nextTick(() => {
        // Reseta accordion bodies dos novos cards
        content.querySelectorAll('[id^="faq-body-"]').forEach((el) => {
          const htmlEl = el as HTMLElement;

          htmlEl.getAnimations().forEach((a) => a.cancel());
          htmlEl.style.maxHeight = '0px';
          htmlEl.style.opacity = '0';
          htmlEl.style.overflow = 'hidden';
        });

        content.animate(
          [
            { opacity: 0, transform: 'translateY(6px)' },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          { duration: 300, easing: 'ease-out', fill: 'forwards' },
        );
      });
    };
  } else {
    activeTab.value = index;
  }
}

// Drag handlers for tabs
function onMouseDownTabs(e: MouseEvent) {
  if (!tabsNavigation.value) return;
  isDragging.value = true;
  startX.value = e.pageX - tabsNavigation.value.offsetLeft;
  scrollLeft.value = tabsNavigation.value.scrollLeft;
  tabsNavigation.value.style.cursor = 'grabbing';
  tabsNavigation.value.style.userSelect = 'none';
  dragged.value = false;
}

function onMouseMoveTabs(e: MouseEvent) {
  if (!isDragging.value || !tabsNavigation.value) return;
  e.preventDefault();

  const x = e.pageX - tabsNavigation.value.offsetLeft;
  const walk = x - startX.value;

  if (Math.abs(walk) > 5) {
    dragged.value = true;
  }
  tabsNavigation.value.scrollLeft = scrollLeft.value - walk;
}

function onMouseUpTabs() {
  if (!isDragging.value) return;
  isDragging.value = false;

  if (tabsNavigation.value) {
    tabsNavigation.value.style.cursor = 'grab';
    tabsNavigation.value.style.userSelect = 'auto';
  }
}

onMounted(() => {
  fetchFaq();
  document.addEventListener('mouseup', onMouseUpTabs);
});

onUnmounted(() => {
  document.removeEventListener('mouseup', onMouseUpTabs);
});
</script>

<template>
  <div
    v-if="hasCategories || loading"
    class="tabsFAQ"
  >
    <PerfilOficialSwiperHeader
      :icon-path="headerIconPath"
      :show-navigation="false"
      :show-see-more="false"
      :title="headerTitle"
    />

    <!-- Loading Skeleton -->
    <template v-if="loading && !hasCategories">
      <!-- Skeleton Tabs -->
      <div class="tabsFAQ__tabs-navigation">
        <div
          v-for="n in 4"
          :key="`skeleton-tab-${n}`"
          class="tabsFAQ__skeleton-tab"
        />
      </div>
      <!-- Skeleton Accordions -->
      <div class="tabsFAQ__content">
        <div
          v-for="n in 3"
          :key="`skeleton-accordion-${n}`"
          class="tabsFAQ__skeleton-card"
        >
          <div class="tabsFAQ__skeleton-card-title" />
          <div class="tabsFAQ__skeleton-card-chevron" />
        </div>
      </div>
    </template>

    <template v-else>
      <!-- Tabs Navigation -->
      <div
        ref="tabsNavigation"
        class="tabsFAQ__tabs-navigation tab-glow--scroll"
        @mousedown="onMouseDownTabs"
        @mousemove="onMouseMoveTabs"
        @mouseup="onMouseUpTabs"
      >
        <!-- Search Tab Button -->
        <button
          ref="searchTabRef"
          class="tabsFAQ__search-tab"
          :class="{ 'tabsFAQ__search-tab--open': searchOpen }"
          @click.stop="toggleSearch"
        >
          <NuxtIcon
            class="tabsFAQ__search-icon"
            :filled="true"
            name="search"
          />
          <input
            v-if="searchOpen"
            ref="searchInputRef"
            v-model="searchQuery"
            class="tabsFAQ__search-input"
            placeholder="Buscar no FAQ..."
            type="text"
            @click.stop
            @mousedown.stop
          />
          <NuxtIcon
            v-if="searchOpen"
            class="tabsFAQ__search-close"
            :filled="true"
            name="close"
            @click.stop="toggleSearch"
          />
        </button>

        <BaseFilterTabButton
          v-for="(category, index) in categories"
          :key="category.id"
          :active="activeTab === index && !searchOpen"
          :label="category.name"
          @click="selectTab(index)"
        />
      </div>

      <!-- Search Results -->
      <div
        v-if="searchOpen"
        class="tabsFAQ__content"
      >
        <div
          v-if="searchQuery.trim() === ''"
          class="tabsFAQ__empty"
        >
          <span>Digite algo para buscar no FAQ</span>
        </div>
        <div
          v-else-if="searchResults.length === 0"
          class="tabsFAQ__empty"
        >
          <span>Nenhum resultado para "{{ searchQuery }}"</span>
        </div>
        <template v-else>
          <div
            v-for="question in searchResults"
            :key="`search-${question.id}`"
            class="tabsFAQ__card"
            :class="{ 'tabsFAQ__card--open': openQuestionId === question.id }"
          >
            <button
              class="tabsFAQ__card-header"
              @click="toggleQuestion(question.id)"
            >
              <div class="tabsFAQ__card-header-inner">
                <span class="tabsFAQ__card-category">{{
                  question.categoryName
                }}</span>
                <span class="tabsFAQ__card-title">{{ question.question }}</span>
              </div>
              <NuxtIcon
                class="tabsFAQ__card-chevron"
                :class="{
                  'tabsFAQ__card-chevron--open': openQuestionId === question.id,
                }"
                :filled="true"
                name="arrows/angle_down"
              />
            </button>
            <div
              :id="`faq-body-${question.id}`"
              style="max-height: 0; overflow: hidden; opacity: 0"
            >
              <div style="padding: 0 16px 14px">
                <div
                  class="tabsFAQ__card-answer"
                  v-html="question.answer"
                />
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Accordion Content (normal tabs) -->
      <div
        v-else-if="currentCategory"
        class="tabsFAQ__content"
      >
        <div
          v-if="currentCategory.items.length === 0"
          class="tabsFAQ__empty"
        >
          <span>Nenhuma pergunta disponível</span>
        </div>

        <template v-else>
          <div
            v-for="question in currentCategory.items"
            :key="question.id"
            class="tabsFAQ__card"
            :class="{ 'tabsFAQ__card--open': openQuestionId === question.id }"
          >
            <button
              class="tabsFAQ__card-header"
              @click="toggleQuestion(question.id)"
            >
              <span class="tabsFAQ__card-title">{{ question.question }}</span>
              <NuxtIcon
                class="tabsFAQ__card-chevron"
                :class="{
                  'tabsFAQ__card-chevron--open': openQuestionId === question.id,
                }"
                :filled="true"
                name="arrows/angle_down"
              />
            </button>
            <div
              :id="`faq-body-${question.id}`"
              style="max-height: 0; overflow: hidden; opacity: 0"
            >
              <div style="padding: 0 16px 14px">
                <div
                  class="tabsFAQ__card-answer"
                  v-html="question.answer"
                />
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<style scoped>
.tabsFAQ {
  border-radius: 8px;
  margin-bottom: 8px;
}

.tabsFAQ__tabs-navigation {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
  position: relative;
  user-select: none;
}

.tabsFAQ__tabs-navigation.tab-glow--scroll {
  padding: 0 14px 18px 14px;
  margin: 0 -14px -8px -14px;
}

.tabsFAQ__tabs-navigation::-webkit-scrollbar {
  display: none;
}

.tabsFAQ__content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ========== ACCORDION CARDS ========== */
.tabsFAQ__card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  transition: border-color 0.2s ease;
}

.tabsFAQ__card--open {
  border-color: rgba(255, 255, 255, 0.1);
}

.tabsFAQ__card-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  cursor: pointer;
  background: none;
  border: none;
  text-align: left;
  color: #ffffff;
  transition: opacity 0.15s ease;
}

.tabsFAQ__card-header:hover {
  opacity: 0.85;
}

.tabsFAQ__card-title {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  flex: 1;
  user-select: none;
}

.tabsFAQ__card-chevron {
  flex-shrink: 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  transition: transform 0.25s ease;
}

.tabsFAQ__card-chevron--open {
  transform: rotate(180deg);
}

.tabsFAQ__card-answer {
  color: rgba(255, 255, 255, 0.55);
  font-size: 13px;
  line-height: 1.7;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding-top: 12px;
  user-select: none;
}

.tabsFAQ__card-answer :deep(a) {
  color: #00c9ff;
  user-select: none;
  -webkit-user-drag: none;
}

.tabsFAQ__card-answer :deep(ul),
.tabsFAQ__card-answer :deep(ol) {
  padding-left: 1.25rem;
  margin: 0.5rem 0;
}

.tabsFAQ__card-answer :deep(ul) {
  list-style-type: disc;
}

.tabsFAQ__card-answer :deep(ol) {
  list-style-type: decimal;
}

.tabsFAQ__card-answer :deep(li) {
  display: list-item;
  margin-bottom: 0.25rem;
}

.tabsFAQ__card-answer :deep(li > p) {
  display: inline;
  margin: 0;
  padding: 0;
}

.tabsFAQ__card-answer :deep(i),
.tabsFAQ__card-answer :deep(em) {
  font-style: italic;
}

.tabsFAQ__card-answer :deep(b),
.tabsFAQ__card-answer :deep(strong) {
  font-weight: 700;
}

.tabsFAQ__card-answer :deep(u) {
  text-decoration: underline;
}

.tabsFAQ__card-answer :deep(s) {
  text-decoration: line-through;
}

/* ========== SEARCH TAB ========== */
.tabsFAQ__search-tab {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  overflow: hidden;
  width: 32px;
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border-radius: 12px;
  background: var(--colors-flat-default-flat, rgba(63, 63, 70, 0.4));
  color: #ffffff;
  cursor: pointer;
  border: none;
  outline: none;
}

.tabsFAQ__search-tab--open {
  background: rgba(255, 255, 255, 0.08);
  padding: 0 10px;
}

.tabsFAQ__search-icon {
  font-size: 14px;
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.7);
}

.tabsFAQ__search-input {
  flex: 1;
  min-width: 0;
  background: none;
  border: none;
  outline: none;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  caret-color: #fff;
}

.tabsFAQ__search-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.tabsFAQ__search-close {
  font-size: 11px;
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
}

.tabsFAQ__search-close:hover {
  color: #fff;
}

.tabsFAQ__card-header-inner {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  text-align: left;
}

.tabsFAQ__card-category {
  font-size: 10px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* ========== EMPTY STATE ========== */
.tabsFAQ__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: rgba(255, 255, 255, 0.5);
}

/* ========== SKELETON ========== */
.tabsFAQ__skeleton-tab {
  width: 80px;
  height: 32px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
  animation: tabsFAQ-pulse 1.5s ease-in-out infinite;
}

.tabsFAQ__skeleton-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  margin-bottom: 8px;
}

.tabsFAQ__skeleton-card-title {
  width: 65%;
  height: 14px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsFAQ-pulse 1.5s ease-in-out infinite;
}

.tabsFAQ__skeleton-card-chevron {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsFAQ-pulse 1.5s ease-in-out infinite;
}

@keyframes tabsFAQ-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

/* ========== DESKTOP ========== */
@media (min-width: 640px) {
  .tabsFAQ {
    margin-bottom: 20px;
  }

  .tabsFAQ__tabs-navigation {
    margin-bottom: 8px;
  }

  .tabsFAQ__search-tab {
    width: 44px;
    min-width: 44px;
    height: 44px;
    padding: 0 14px;
  }

  .tabsFAQ__search-tab--open {
    padding: 0 14px;
  }

  .tabsFAQ__search-icon {
    font-size: 16px;
  }

  .tabsFAQ__search-input {
    font-size: 13px;
  }

  .tabsFAQ__card-title {
    font-size: 14px;
  }

  .tabsFAQ__card-answer {
    font-size: 14px;
  }
}

/* ========== MOBILE ========== */
@media (max-width: 414px) {
  .tabsFAQ__tabs-navigation.tab-glow--scroll {
    margin-right: calc(-1 * var(--container-padding, 16px));
    padding-right: var(--container-padding, 16px);
  }
}
</style>
